import { Accordion } from 'govuk-frontend';

import { Component, ElementRef, OnDestroy, QueryList, ViewChild, ViewChildren} from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription, map } from 'rxjs';

// common
import { IAdviceSet, IQuestionResponse, LinkNavigationService, TreeItemType, IAdviceLink, AdviceDoc, IDocExportRequest, TreeType } from '@aa/common';

// shared
import { QnaStage, PdfDownloadService, ShareService, AdviceState, SelfServAnalyticsService } from '@aa/selfserv/shared';

// qna
import { AbstractQnaComponent } from '../abstract-qna.component';
import { QnaNavigationService } from '../qna-navigation.service';

interface SelfServAdviceDoc extends AdviceDoc {
  heading: string;
  quickExit: boolean;
}

@Component({
  selector: 'aa-qna-advice',
  templateUrl: './advice.component.html'
})
export class QnaAdviceComponent extends AbstractQnaComponent implements OnDestroy {

  public readonly TreeType = TreeType;

  @ViewChildren('adviceItem') adviceElements: QueryList<ElementRef>;
  @ViewChild('description') descriptionElement: ElementRef;
  @ViewChild('accordion') accordionElement: ElementRef;

  private detailsClickedTimeout: any = null;

  public result: SelfServAdviceDoc | null = null;
  public showEmailForm: boolean = false;

  public downloadActive: boolean = false;
  public downloadError: boolean = false;
  private downloadSubscription: Subscription;

  private govukAccordion: Accordion = null; // TODO: remove all accordion refs

  public showExitBtn: boolean = false;

  constructor(navigation: QnaNavigationService, route: ActivatedRoute,
              private readonly shareService: ShareService,
              private readonly pdfDownload: PdfDownloadService,
              private readonly analytics: SelfServAnalyticsService,
              private readonly linkService: LinkNavigationService) {
    super(navigation, 'Advice', QnaStage.Advice);

    const result$ = route.paramMap.pipe(map(o => o.get('result')));

    const state = window.history.state as AdviceState;
    if (state.questionsComplete) {
      // referrer was questions page
      // Might use this to provide additional tracking info in the future
    }

    super.subscribe(result$, resultId => {
      this.init(resultId);
    });
  }

  public get canShare(): boolean {
    return this.result && this.shareService.canShare;
  }

  public share() {
    this.shareService.share(this.result);
    return false; // disable event propagation
  }

  public ngOnDestroy(): void {
    super.ngOnDestroy();
    clearTimeout(this.detailsClickedTimeout);

    // unsubscribe to any pending pdf downloads
    this.downloadSubscription?.unsubscribe();

    this.govukAccordion = null;
  }

  public toggleEmailForm() {
    this.showEmailForm = !this.showEmailForm;
    return false; // for link handlers
  }

  private init(resultId: string) {

    this.result = this.getAdviceResult(resultId);

    if (this.result ) {
      this.navigation.setTitle(this.result.name);
      this.navigation.setCurrentSubPage(this.result.name);

      this.analytics.trackAdviceView(this.result.name);
    }

    this.navigation.scrollTop();

    this.showExitBtn = this.shouldShowExitButton();
    if(this.showExitBtn) {
      console.log("Exit button enabled");
    }

    setTimeout(() => {
      this.initAccordion();
      this.focusDescriptionElement();
    });
  }

  private shouldShowExitButton(): boolean {
    if(!this.result) {
      return false;
    }

    return this.result.quickExit;
  }

  private initAccordion() {
    if (!this.accordionElement) {
      return;
    }

    if (this.govukAccordion) {
      const controls = this.govukAccordion.$module.getElementsByClassName('govuk-accordion__controls');
      controls[0].remove();
    }

    this.govukAccordion = new Accordion(this.accordionElement.nativeElement);

    this.govukAccordion.init();
  }

  private focusDescriptionElement() {
    // const el: ElementRef = this.adviceElements.first;
    const el: ElementRef = this.descriptionElement;
    if (!el) {
      return;
    }

    el.nativeElement.focus();
  }

  public handleClick(e: MouseEvent): void {
    this.linkService.handleClick(e);
  }
  /**
   * Search for result using slug or guid
   * @param resultId
   */
  private getAdviceResult(resultId: string): SelfServAdviceDoc {

    const results = this.data.adviceSets;
    if (!results) {
      console.warn('No advice results available');
      return null;
    }

    const adviceSet = results.find(o => o.slug === resultId || o.id === resultId);

    if (!adviceSet) {
      console.warn('Failed to resolve advice result: ' + resultId);
      return null;
    }

    return {
      adviceId: adviceSet.id,
      slug: adviceSet.slug,
      href: this.navigation.getAdviceSetHrefById(adviceSet.id),
      name: adviceSet.name,
      heading: adviceSet.description,
      paragraphs: this.data.getParagraphs(adviceSet.snippetIds),
      relatedAdvice: this.renderRelatedAdviceLinks(adviceSet),
      quickExit: adviceSet.quickExit ?? false
    };
  }

  private renderRelatedAdviceLinks(result: IAdviceSet): IAdviceLink[] {
    if (!result.relatedAdvice) {
      return [];
    }

    return result.relatedAdvice.map(o => {

      const href = this.getRelatedAdviceHref(o);

      return {
        text: o.text,
        href: href,
        isExternal: href && href.startsWith('http')
      };
    });
  }

  private getRelatedAdviceHref(item: IQuestionResponse): string {

    const id = item.nextId;

    switch (item.nextType) {

      case TreeItemType.Link:
        return item.href;

      case TreeItemType.AdviceSet:
        return this.navigation.getAdviceSetHrefById(id);

      case TreeItemType.Question:
      default:
        return this.navigation.getQuestionHrefById(id);
    }
  }

  public downloadPdf() {
    if (this.downloadActive) {
      return false;
    }

    this.downloadActive = true;

    const req = {
      doc: this.result,
      meta: this.navigation.meta
    } as IDocExportRequest;

    this.downloadSubscription = this.pdfDownload.download(req).subscribe(res => {
      this.downloadError = false;
      this.downloadActive = false;
      this.analytics.trackPdfExport(this.result);
    }, err => {
      this.downloadError = true;
      this.downloadActive = false;
      this.analytics.trackPdfError(this.result, err);
    });

    // to prevent navigation
    return false;
  }
}
